import React from 'react'
import Markdown from './Markdown'
import usePlayerStore from '../stores/playerStore'
import { canEmbed } from './videoEmbed'

// Source-aware document formatting for the DocumentViewer. The indexed text is
// stored structure-poor (transcripts are walls of sentences; SRT chunks carry a
// "[Video: X] [t1 - t2]" header the loader injected), so each source kind gets
// its own presentation. Detection is render-time (extension → content sniff →
// fallback) until an explicit content_kind lands in chunk metadata (Phase 2 of
// doc/plan-timestamped-sources.md).

// ---------- detection ----------

export function detectDocKind(source, text) {
  const name = String(source || '').toLowerCase()
  if (/\.(srt|vtt)$/.test(name)) return 'subtitles'
  if (/\.(md|markdown)$/.test(name)) return 'markdown'
  if (/^\s*webvtt/i.test(text || '')) return 'subtitles'
  if (/\.txt$/.test(name)) {
    if (/_transcript|_translation/.test(name)) return 'transcript'
    if (looksLikeWall(text)) return 'transcript'
  }
  return 'plain'
}

export const KIND_LABEL = {
  subtitles: 'subtitles',
  transcript: 'transcript',
  markdown: 'markdown',
  plain: 'text',
}

// A "wall" = prose with no paragraph structure (huge average line length), the
// shape a concatenated transcript has. Real articles/notes break lines sooner.
function looksLikeWall(text) {
  if (!text) return false
  const sample = text.slice(0, 20000)
  const lines = sample.split('\n')
  return sample.length / lines.length > 300
}

// ---------- source deep links ----------

// Origin-aware link into the source video, optionally at a time offset.
// Returns null when the doc has no web URL (local/drive uploads) — callers
// hide the affordance. Timestamp params differ per platform: YouTube takes
// `t=NNs`, Rutube `t=NN`; a TikTok *web* URL has no seek param (the in-app
// embed player does — see ./videoEmbed), so it links to the whole video.
export function sourceLink(meta, seconds) {
  const url = meta?.source_url
  if (!url) return null
  const origin = String(meta?.origin || '').toLowerCase()
  const t = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : null
  const sep = url.includes('?') ? '&' : '?'
  if (t == null || t === 0) return url
  if (origin.startsWith('youtube')) return `${url}${sep}t=${t}s`
  if (origin === 'rutube') return `${url}${sep}t=${t}`
  return url
}

// ---------- block building ----------

// SRTLoader prefixes every chunk with "[Video: name] [HH:MM:SS - HH:MM:SS]\n\n".
const SRT_HEADER_RE = /^\[Video: [^\]]*\] \[(\d{2}:\d{2}:\d{2}) - (\d{2}:\d{2}:\d{2})\]\s*\n+/

// Strip the injected header off a single chunk's text (search snippets, viewer
// blocks). Safe on non-subtitle text — the regex simply won't match.
export function stripChunkHeader(text) {
  return String(text || '').replace(SRT_HEADER_RE, '')
}

// Longest suffix-of-prev == prefix-of-next, capped at the chunker's overlap.
function overlapLen(prev, next) {
  const max = Math.min(prev.length, next.length, 400)
  for (let k = max; k > 0; k--) {
    if (prev.endsWith(next.slice(0, k))) return k
  }
  return 0
}

// Slice the reassembled text into per-chunk blocks using the spans get_document
// returns. Blocks are what the kind-specific views render, and what the chunk
// highlight attaches to (block-level, no character offset math).
export function buildBlocks(data, { stripHeaders = false } = {}) {
  const text = data?.text || ''
  const spans = Array.isArray(data?.spans) && data.spans.length ? data.spans : null
  const raw = spans
    ? spans.map((s) => ({
        chunkIndex: s.chunk_index,
        text: text.slice(s.start, s.end),
        // present once get_document returns per-chunk times (plan Phase 2)
        startTime: s.start_time ?? null,
        endTime: s.end_time ?? null,
        timeRange: s.start_time_formatted ? [s.start_time_formatted, s.end_time_formatted] : null,
      }))
    : [{ chunkIndex: null, text, startTime: null, endTime: null, timeRange: null }]
  if (!stripHeaders) return raw
  // Subtitle docs: lift the injected header into the block's time label, then
  // trim the chunk overlap that the header prevented get_document from
  // de-duplicating (its overlap match fails on the "[Video:" prefix).
  let tail = ''
  return raw.map((b) => {
    let t = b.text
    let timeRange = b.timeRange
    const m = t.match(SRT_HEADER_RE)
    if (m) {
      timeRange = [m[1], m[2]]
      t = t.slice(m[0].length)
    }
    t = t.slice(overlapLen(tail, t))
    tail = (tail + t).slice(-600)
    return { ...b, text: t, timeRange }
  })
}

// Group a wall of sentences into readable paragraphs (presentation only — the
// stored text is untouched). ~2-4 sentences per paragraph at default target.
export function toParagraphs(text, target = 380) {
  const sentences = String(text).split(/(?<=[.!?…])\s+/)
  const out = []
  let cur = ''
  for (const s of sentences) {
    cur = cur ? `${cur} ${s}` : s
    if (cur.length >= target) {
      out.push(cur)
      cur = ''
    }
  }
  if (cur) out.push(cur)
  return out
}

// ---------- views ----------

const blockCls = (hl) =>
  `rounded-lg px-3 -mx-3 ${
    hl ? 'bg-gold/10 shadow-[0_0_0_1px_rgba(201,162,39,0.45)] py-2 my-1' : ''
  }`

export function TranscriptView({ blocks, highlightChunkIndex, markRef }) {
  return (
    <div className="px-6 py-5 max-w-[68ch] text-[15px] leading-7 text-ink/90 dark:text-mist/90">
      {blocks.map((b, i) => {
        const hl = highlightChunkIndex != null && b.chunkIndex === highlightChunkIndex
        return (
          <div key={i} ref={hl ? markRef : null} className={blockCls(hl)}>
            {toParagraphs(b.text).map((p, j) => (
              <p key={j} className="mb-3 break-words">
                {p}
              </p>
            ))}
          </div>
        )
      })}
    </div>
  )
}

// Faithful pretty-print: cue lines stay lines (no invented paragraphs); each
// chunk block gets its time-range label. With source_url/origin on the doc
// (docMeta), the label opens the EMBEDDED player at the block's start time
// (external link when the platform has no seekable embed).
export function SubtitlesView({ blocks, highlightChunkIndex, markRef, docMeta }) {
  const openPlayer = usePlayerStore((s) => s.openPlayer)
  const labelCls =
    'mb-1.5 inline-block font-mono text-[11px] tracking-wide text-gold-deep dark:text-gold/80'
  const embeddable = canEmbed(docMeta)
  return (
    <div className="px-6 py-5 text-[14px] leading-7 text-ink/90 dark:text-mist/90">
      {blocks.map((b, i) => {
        const hl = highlightChunkIndex != null && b.chunkIndex === highlightChunkIndex
        const href = sourceLink(docMeta, b.startTime)
        const label = b.timeRange && `${b.timeRange[0]} – ${b.timeRange[1]}`
        return (
          <div key={i} ref={hl ? markRef : null} className={`${blockCls(hl)} mb-4`}>
            {label &&
              (embeddable && b.startTime != null ? (
                <button
                  onClick={() =>
                    openPlayer({
                      source_url: docMeta.source_url,
                      origin: docMeta.origin,
                      video_id: docMeta.video_id,
                      title: docMeta.title || docMeta.video_name,
                      start: b.startTime,
                    })
                  }
                  title="Watch the source at this moment"
                  className={`${labelCls} hover:underline`}
                >
                  ▶ {label}
                </button>
              ) : href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  title="Open the video at this moment"
                  className={`${labelCls} hover:underline`}
                >
                  ▶ {label}
                </a>
              ) : (
                <div className={labelCls}>{label}</div>
              ))}
            <div className="whitespace-pre-wrap break-words border-l-2 border-parchment-border dark:border-midnight-600 pl-3">
              {b.text}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function MarkdownView({ text }) {
  return (
    <div className="px-6 py-5">
      <Markdown>{text}</Markdown>
    </div>
  )
}
